import { useState, useEffect, useRef } from 'react';
import { Search, Info, Network, Cpu, Globe, Wifi, CheckCircle, AlertTriangle, Server, Share2, Lock, Users, BarChart4, PieChart, Activity, Map } from 'lucide-react';
import * as d3 from 'd3';

// Animated counter component
const AnimatedCounter = ({ end, duration = 1000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTime = useRef(null);

  useEffect(() => {
    startTime.current = Date.now();
    const endValue = parseInt(end, 10) || 0;
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuad = t => t * (2 - t);
      const easedProgress = easeOutQuad(progress);
      
      countRef.current = Math.floor(easedProgress * endValue);
      setCount(countRef.current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration]);
  
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// SubnetVisualizer component for graphical representation
const SubnetVisualizer = ({ subnets, totalHosts }) => {
  const chartRef = useRef(null);
  
  useEffect(() => {
    if (!chartRef.current || !subnets || subnets.length === 0) return;
    
    // Clear any existing SVG
    d3.select(chartRef.current).selectAll("*").remove();
    
    const width = chartRef.current.clientWidth;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    
    // Create SVG
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // Create scales
    const x = d3.scaleBand()
      .domain(subnets.slice(0, 10).map((_, i) => `Subnet ${i + 1}`))
      .range([0, chartWidth])
      .padding(0.3);
    
    const y = d3.scaleLinear()
      .domain([0, d3.max(subnets.slice(0, 10), subnet => {
        const firstIP = subnet.FirstUsableHost.split('.').map(Number);
        const lastIP = subnet.LastUsableHost.split('.').map(Number);
        return (lastIP[3] - firstIP[3]) + 1;
      })])
      .nice()
      .range([chartHeight, 0]);
    
    // Add x-axis
    svg.append("g")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("fill", "#94a3b8")
      .style("text-anchor", "middle");
    
    // Add y-axis
    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", "#94a3b8");
    
    // Add y-axis label
    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - (chartHeight / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("fill", "#94a3b8")
      .text("Available Hosts");
    
    // Add bars
    svg.selectAll(".bar")
      .data(subnets.slice(0, 10))
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (_, i) => x(`Subnet ${i + 1}`))
      .attr("width", x.bandwidth())
      .attr("y", subnet => {
        const firstIP = subnet.FirstUsableHost.split('.').map(Number);
        const lastIP = subnet.LastUsableHost.split('.').map(Number);
        const hostCount = (lastIP[3] - firstIP[3]) + 1;
        return y(hostCount);
      })
      .attr("height", subnet => {
        const firstIP = subnet.FirstUsableHost.split('.').map(Number);
        const lastIP = subnet.LastUsableHost.split('.').map(Number);
        const hostCount = (lastIP[3] - firstIP[3]) + 1;
        return chartHeight - y(hostCount);
      })
      .attr("fill", "url(#gradient)");
    
    // Add gradient
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "gradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");
    
    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#3b82f6")
      .attr("stop-opacity", 1);
    
    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#60a5fa")
      .attr("stop-opacity", 0.8);
    
  }, [subnets, totalHosts]);
  
  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
      <h3 className="text-lg font-semibold mb-3 text-white flex items-center">
        <BarChart4 className="w-5 h-5 mr-2 text-blue-400" />
        Subnet Distribution
      </h3>
      <div 
        ref={chartRef} 
        className="w-full h-[300px]"
      ></div>
      <div className="text-sm text-gray-400 mt-2 text-center">
        {subnets && subnets.length > 10 ? "Displaying first 10 subnets" : ""}
      </div>
    </div>
  );
};

// Network Map visualization
const NetworkMap = ({ results }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !results) return;
    
    const width = canvasRef.current.clientWidth;
    const height = 300;
    
    // Clear any existing SVG
    d3.select(canvasRef.current).selectAll("*").remove();
    
    // Create SVG
    const svg = d3.select(canvasRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height);
    
    // Add gradient background
    const defs = svg.append("defs");
    
    const gradient = defs.append("linearGradient")
      .attr("id", "network-bg")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "100%");
    
    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#1e293b")
      .attr("stop-opacity", 1);
    
    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#0f172a")
      .attr("stop-opacity", 1);
    
    // Add background
    svg.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "url(#network-bg)")
      .attr("rx", 8)
      .attr("ry", 8);
    
    // Define nodes and links
    const nodes = [
      { id: "internet", name: "Internet", type: "internet", x: width * 0.5, y: height * 0.2 },
      { id: "gateway", name: "Gateway", type: "gateway", x: width * 0.5, y: height * 0.5 },
      { id: "network", name: results.network_address, type: "network", x: width * 0.3, y: height * 0.8 },
      { id: "broadcast", name: results.broadcast_address, type: "broadcast", x: width * 0.7, y: height * 0.8 }
    ];
    
    const links = [
      { source: "internet", target: "gateway" },
      { source: "gateway", target: "network" },
      { source: "gateway", target: "broadcast" }
    ];
    
    // Draw links
    svg.selectAll(".link")
      .data(links)
      .enter()
      .append("line")
      .attr("class", "link")
      .attr("x1", d => nodes.find(n => n.id === d.source).x)
      .attr("y1", d => nodes.find(n => n.id === d.source).y)
      .attr("x2", d => nodes.find(n => n.id === d.target).x)
      .attr("y2", d => nodes.find(n => n.id === d.target).y)
      .attr("stroke", "#4b5563")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", d => d.source === "internet" ? "5,5" : "none");
    
    // Draw nodes
    const nodeGroups = svg.selectAll(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.x},${d.y})`);
    
    // Node circles
    nodeGroups.append("circle")
      .attr("r", 25)
      .attr("fill", d => {
        switch(d.type) {
          case "internet": return "#3b82f6";
          case "gateway": return "#10b981";
          case "network": return "#6366f1";
          case "broadcast": return "#f59e0b";
          default: return "#64748b";
        }
      })
      .attr("stroke", "#0f172a")
      .attr("stroke-width", 2);
    
    // Node icons
    nodeGroups.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", ".3em")
      .attr("fill", "white")
      .attr("font-family", "sans-serif")
      .attr("font-size", "14px")
      .text(d => {
        switch(d.type) {
          case "internet": return "🌐";
          case "gateway": return "🔌";
          case "network": return "📡";
          case "broadcast": return "📢";
          default: return "•";
        }
      });
    
    // Node labels
    nodeGroups.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "3em")
      .attr("fill", "#e2e8f0")
      .attr("font-family", "sans-serif")
      .attr("font-size", "12px")
      .text(d => d.name);
    
  }, [results]);
  
  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
      <h3 className="text-lg font-semibold mb-3 text-white flex items-center">
        <Map className="w-5 h-5 mr-2 text-green-400" />
        Network Topology
      </h3>
      <div 
        ref={canvasRef} 
        className="w-full h-[300px]"
      ></div>
    </div>
  );
};

// IP Usage Donut Chart
const IPUsageChart = ({ totalHosts, usableHosts }) => {
  const chartRef = useRef(null);
  
  useEffect(() => {
    if (!chartRef.current || !totalHosts || !usableHosts) return;
    
    // Clear any existing SVG
    d3.select(chartRef.current).selectAll("*").remove();
    
    const width = chartRef.current.clientWidth;
    const height = 300;
    const margin = 40;
    const radius = Math.min(width, height) / 2 - margin;
    
    // Create SVG
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);
    
    // Data
    const data = [
      { name: "Usable", value: usableHosts },
      { name: "Reserved", value: totalHosts - usableHosts }
    ];
    
    // Color scale
    const color = d3.scaleOrdinal()
      .domain(data.map(d => d.name))
      .range(["#3b82f6", "#475569"]);
    
    // Create pie layout
    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);
    
    // Create arc generator
    const arc = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);
    
    // Create arcs
    const arcs = svg.selectAll("arc")
      .data(pie(data))
      .enter()
      .append("g");
    
    // Add paths
    arcs.append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.name))
      .attr("stroke", "#0f172a")
      .attr("stroke-width", 2)
      .style("opacity", 0.9);
    
    // Add text labels
    arcs.append("text")
      .attr("transform", d => `translate(${arc.centroid(d)})`)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("fill", "#ffffff")
      .text(d => `${d.data.name}`);
    
    // Add center text
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .attr("fill", "#ffffff")
      .text(`${usableHosts}`);
    
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("fill", "#d1d5db")
      .attr("y", "20")
      .text("Usable Hosts");
    
  }, [totalHosts, usableHosts]);
  
  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
      <h3 className="text-lg font-semibold mb-3 text-white flex items-center">
        <PieChart className="w-5 h-5 mr-2 text-blue-400" />
        IP Address Allocation
      </h3>
      <div 
        ref={chartRef} 
        className="w-full h-[300px]"
      ></div>
      <div className="flex justify-center space-x-6 mt-2">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
          <span className="text-sm text-gray-300">Usable ({usableHosts})</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
          <span className="text-sm text-gray-300">Reserved ({totalHosts - usableHosts})</span>
        </div>
      </div>
    </div>
  );
};

// IP address validator component
const IpValidator = ({ subnets }) => {
  const [ipToCheck, setIpToCheck] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  
  const checkIp = () => {
    if (!ipToCheck) return;
    
    // Basic IP validation
    const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    if (!ipv4Regex.test(ipToCheck)) {
      setValidationResult({ valid: false, message: 'Invalid IP address format' });
      return;
    }
    
    // Check each octet is between 0-255
    const octets = ipToCheck.split('.');
    const validOctets = octets.every(octet => {
      const num = parseInt(octet, 10);
      return num >= 0 && num <= 255;
    });
    
    if (!validOctets) {
      setValidationResult({ valid: false, message: 'IP octets must be between 0-255' });
      return;
    }
    
    // Check if IP is in any subnet
    let inSubnet = false;
    let matchingSubnet = null;
    
    if (subnets && subnets.length) {
      for (const subnet of subnets) {
        // Convert IP addresses to numbers for comparison
        const ipToNum = (ip) => {
          return ip.split('.')
            .reduce((sum, octet, index) => sum + (parseInt(octet, 10) * Math.pow(256, 3-index)), 0);
        };
        
        const checkIpNum = ipToNum(ipToCheck);
        const firstUsableNum = ipToNum(subnet.FirstUsableHost);
        const lastUsableNum = ipToNum(subnet.LastUsableHost);
        
        if (checkIpNum >= firstUsableNum && checkIpNum <= lastUsableNum) {
          inSubnet = true;
          matchingSubnet = subnet;
          break;
        }
      }
    }
    
    if (inSubnet) {
      setValidationResult({ 
        valid: true, 
        message: `IP is valid and belongs to subnet ${matchingSubnet.Subnet}`,
        subnet: matchingSubnet
      });
    } else {
      setValidationResult({ 
        valid: true, 
        message: 'IP is valid but does not belong to any calculated subnet' 
      });
    }
  };
  
  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
      <h3 className="text-lg font-semibold mb-3 flex items-center text-white">
        <CheckCircle className="w-5 h-5 mr-2 text-blue-400" />
        IP Validator
      </h3>
      <div className="flex items-center space-x-2">
        <input
          type="text"
          placeholder="Enter IP to validate (e.g. 192.168.1.1)"
          value={ipToCheck}
          onChange={(e) => setIpToCheck(e.target.value)}
          className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-md shadow-sm text-white placeholder-gray-400"
        />
        <button 
          onClick={checkIp}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Validate
        </button>
      </div>
      
      {validationResult && (
        <div className={`mt-4 p-4 rounded ${
          validationResult.valid && validationResult.subnet 
            ? 'bg-green-900/30 text-green-300 border border-green-700/50' 
            : validationResult.valid 
              ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' 
              : 'bg-red-900/30 text-red-300 border border-red-700/50'
        }`}>
          <div className="flex items-center">
            {validationResult.valid && validationResult.subnet ? 
              <CheckCircle className="w-5 h-5 mr-2" /> : 
              validationResult.valid ? 
                <Info className="w-5 h-5 mr-2" /> :
                <AlertTriangle className="w-5 h-5 mr-2" />
            }
            <p>{validationResult.message}</p>
          </div>
          
          {validationResult.subnet && (
            <div className="mt-3 text-sm grid grid-cols-2 gap-2 font-mono">
              <div>Network: {validationResult.subnet.NetworkAddress}</div>
              <div>Broadcast: {validationResult.subnet.BroadcastAddress}</div>
              <div>First host: {validationResult.subnet.FirstUsableHost}</div>
              <div>Last host: {validationResult.subnet.LastUsableHost}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// SubnetCalculatorResults Component
export default function SubnetResultVisualizer({ results }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSubnets, setFilteredSubnets] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  //console.log("results", results.subnets_info);

  useEffect(() => {
    if (results?.subnets_info) {
      setFilteredSubnets(results.subnets_info);
    }
  }, [results]);

  useEffect(() => {
    if (results?.subnets_info && searchTerm) {
      const filtered = results.subnets_info.filter(subnet => 
        subnet.NetworkAddress.includes(searchTerm) || 
        subnet.Subnet.includes(searchTerm)
      );
      setFilteredSubnets(filtered);
    } else if (results?.subnets_info) {
      setFilteredSubnets(results.subnets_info);
    }
  }, [searchTerm, results]);

  if (!results) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-900 rounded-lg">
        <p className="text-gray-400">No calculation results to display.</p>
      </div>
    );
  }

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
        activeTab === id
          ? 'bg-blue-900/60 text-blue-300 border-b-2 border-blue-500'
          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-300'
      }`}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </button>
  );

  const getClassColor = (ipClass) => {
    const classColors = {
      'Class A': 'text-green-400',
      'Class B': 'text-blue-400',
      'Class C': 'text-purple-400',
      'Class D': 'text-orange-400',
      'Class E': 'text-red-400'
    };
    return classColors[ipClass] || 'text-gray-400';
  };

  return (
    <div className="bg-gray-900 rounded-xl mx-auto shadow-xl overflow-hidden text-gray-200 border border-gray-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-6">
        <h2 className="text-2xl font-bold flex items-center text-white">
          <Network className="w-6 h-6 mr-2" />
          Subnet Calculation Results
        </h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-blue-800/30">
            <div className="text-sm font-medium opacity-80">IP Address</div>
            <div className="text-xl font-mono text-white">{results.result.ip_address}</div>
          </div>
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-blue-800/30">
            <div className="text-sm font-medium opacity-80">CIDR Notation</div>
            <div className="text-xl font-mono text-white">{results.result.cidr_notation}</div>
          </div>
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-blue-800/30">
            <div className="text-sm font-medium opacity-80">Subnet Mask</div>
            <div className="text-xl font-mono text-white">{results.result.subnet_mask}</div>
          </div>
        </div>
      </div>

      {/* IP Validator Section */}
      <div className="px-6 pt-6">
        <IpValidator subnets={results.subnets_info} />
      </div>
      
      {/* Tabs Navigation */}
      <div className="px-6 border-b border-gray-800">
        <div className="flex space-x-1 overflow-x-auto">
          <TabButton 
            id="overview" 
            label="Overview" 
            icon={<Info className="w-4 h-4" />} 
          />
          <TabButton 
            id="visualization" 
            label="Visualization" 
            icon={<Activity className="w-4 h-4" />} 
          />
          <TabButton 
            id="subnets" 
            label="Subnets" 
            icon={<Wifi className="w-4 h-4" />} 
          />
          <TabButton 
            id="technical" 
            label="Technical" 
            icon={<Cpu className="w-4 h-4" />} 
          />
          <TabButton 
            id="vlan" 
            label="VLAN" 
            icon={<Share2 className="w-4 h-4" />} 
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                  <Globe className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Network Class</h3>
                </div>
                <p className={`text-2xl font-semibold ${getClassColor(results.result.ip_class)}`}>
                  {results.result.ip_class}
                </p>
                <p className="text-sm text-gray-400 mt-1">Type: {results.result.ip_type}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                  <Server className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Network Address</h3>
                </div>
                <p className="text-2xl font-mono font-semibold">{results.result.network_address}</p>
                <p className="text-sm text-gray-400 mt-1">CIDR: {results.result.network_address + results.result.cidr_notation}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                  <Wifi className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Broadcast Address</h3>
                </div>
                <p className="text-2xl font-mono font-semibold">{results.result.broadcast_address}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                  <Users className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Total Hosts</h3>
                </div>
                <p className="text-2xl font-semibold text-white">
                  <AnimatedCounter end={results.result.total_hosts} duration={2000} />
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Usable: <AnimatedCounter end={results.result.usable_hosts} duration={2000} />
                </p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors col-span-1 md:col-span-2">
                <div className="flex items-center mb-2">
                  <Network className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Usable Host Range</h3>
                </div>
                <p className="text-xl font-mono font-semibold">{results.result.usable_host_ip_range}</p>
              </div>
            </div>
            
            {/* Wildcard and Mask */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                    <CheckCircle className="w-5 h-5 text-blue-400 mr-2" />  
                  <h3 className="font-medium text-gray-200">Subnet Mask</h3>
                </div>
                <p className="text-xl font-mono font-semibold">{results.result.subnet_mask}</p>
                <p className="text-sm text-gray-400 mt-1">Binary: {results.result.binary_subnet_mask}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700 hover:border-blue-800 transition-colors">
                <div className="flex items-center mb-2">
                  <Lock className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="font-medium text-gray-200">Wildcard Mask</h3>
                </div>
                <p className="text-xl font-mono font-semibold">{results.result.wildcard_mask}</p>
              </div>
            </div>
            
            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
                <h3 className="font-medium text-gray-200 mb-2">Binary ID</h3>
                <p className="font-mono text-sm break-all text-gray-400">{results.result.binary_id}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
                <h3 className="font-medium text-gray-200 mb-2">Hexadecimal</h3>
                <p className="font-mono text-lg text-gray-400">{results.result.hex_id}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
                <h3 className="font-medium text-gray-200 mb-2">Integer ID</h3>
                <p className="font-mono text-lg text-gray-400">{results.result.integer_id}</p>
              </div>
            </div>
            
            {/* IPv6 Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
                <h3 className="font-medium text-gray-200 mb-2">IPv4-Mapped Address</h3>
                <p className="font-mono text-sm break-all text-gray-400">{results.result.ipv4_mapped_address}</p>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
                <h3 className="font-medium text-gray-200 mb-2">6to4 Prefix</h3>
                <p className="font-mono text-sm break-all text-gray-400">{results.result.sixtofour_prefix}</p>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
              <h3 className="font-medium text-gray-200 mb-2">Reverse DNS (ARPA)</h3>
              <p className="font-mono text-lg text-gray-400">{results.result.in_addr_arpa}</p>
            </div>
          </div>
        )}

        {/* Visualization Tab */}
        {activeTab === 'visualization' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <IPUsageChart 
                totalHosts={results.result.total_hosts} 
                usableHosts={results.result.usable_hosts} 
              />
              
              <NetworkMap results={results} />
            </div>
            
            <SubnetVisualizer 
              subnets={results.subnets_info} 
              totalHosts={results.result.total_hosts} 
            />
            
            {/* Network Statistics Card */}
            <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold mb-4 text-white flex items-center">
                <BarChart4 className="w-5 h-5 mr-2 text-blue-400" />
                Network Statistics
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-900 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-400">Network Class</p>
                  <p className={`text-lg font-semibold ${getClassColor(results.result.ip_class)}`}>
                    {results.result.ip_class}
                  </p>
                </div>
                
                <div className="bg-gray-900 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-400">Total Subnets</p>
                  <p className="text-lg font-semibold text-blue-400">
                    {results.subnets_info.length}
                  </p>
                </div>
                
                <div className="bg-gray-900 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-400">Host Utilization</p>
                  <p className="text-lg font-semibold text-green-400">
                    {((results.result.usable_hosts / results.result.total_hosts) * 100).toFixed(2)}%
                  </p>
                </div>
                
                <div className="bg-gray-900 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-400">Reserved IPs</p>
                  <p className="text-lg font-semibold text-orange-400">
                    {results.result.total_hosts - results.result.usable_hosts}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subnets Tab */}
        {activeTab === 'subnets' && (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-700 rounded-md bg-gray-800 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search subnets by network address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Subnets Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-700">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">#</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Subnet</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Network Address</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">First Usable Host</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Usable Host</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Broadcast Address</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP Increment</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-900 divide-y divide-gray-800">
                  {filteredSubnets.map((subnet, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-900' : 'bg-gray-800/50'}>
                        <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{index + 1}</td> 
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.Subnet}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.NetworkAddress}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.FirstUsableHost}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.LastUsableHost}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.BroadcastAddress}</td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-300">{subnet.IP_Increment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredSubnets.length === 0 && (
              <div className="bg-gray-800 p-8 rounded-lg text-center">
                <p className="text-gray-400">No subnets found matching your search criteria.</p>
              </div>
            )}
            
            <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4 text-sm text-blue-300">
              <div className="flex">
                <Info className="flex-shrink-0 h-5 w-5 mr-2" />
                <p>
                  <span className="font-semibold">Note:</span> The IP Increment column shows the offset for each subnet from the base network address.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Technical Tab */}
        {activeTab === 'technical' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-3 text-white">Binary Representation</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">IP Address Binary</p>
                    <p className="font-mono text-xs bg-gray-900 p-2 rounded overflow-auto">{results.result.binary_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Subnet Mask Binary</p>
                    <p className="font-mono text-xs bg-gray-900 p-2 rounded overflow-auto">{results.result.binary_subnet_mask}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-3 text-white">Alternative Representations</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Hexadecimal</p>
                      <p className="font-mono bg-gray-900 p-2 rounded">{results.result.hex_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Integer</p>
                      <p className="font-mono bg-gray-900 p-2 rounded">{results.result.integer_id}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">IPv4-Mapped IPv6</p>
                    <p className="font-mono text-xs bg-gray-900 p-2 rounded">{results.result.ipv4_mapped_address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">6to4 Prefix</p>
                    <p className="font-mono text-xs bg-gray-900 p-2 rounded">{results.result.sixtofour_prefix}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold mb-3 text-white">Network Calculations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Total Hosts</p>
                  <p className="font-mono bg-gray-900 p-2 rounded">{results.result.total_hosts.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Formula: 2^(32-prefix_length)</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Usable Hosts</p>
                  <p className="font-mono bg-gray-900 p-2 rounded">{results.result.usable_hosts.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Formula: 2^(32-prefix_length) - 2</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold mb-3 text-white">DNS Information</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Reverse DNS (PTR) Format</p>
                  <p className="font-mono bg-gray-900 p-2 rounded">{results.result.in_addr_arpa}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VLAN Tab */}
        {activeTab === 'vlan' && (
          <div className="space-y-6">
            {results.vlan_info && results.vlan_info.length > 0 && (
              <div className="bg-gray-800 p-4 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mb-3 text-white flex items-center">
                  <Share2 className="w-5 h-5 mr-2 text-blue-400" />
                  VLAN Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-medium text-gray-300 mb-2">Native VLAN</h4>
                    <p className="text-2xl font-mono font-semibold text-blue-400">
                      {results.vlan_info[0].Native_VLAN}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Default untagged VLAN</p>
                  </div>
                  
                  <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-medium text-gray-300 mb-2">Reserved VLANs</h4>
                    <p className="text-xl font-mono font-semibold text-orange-400">
                      {results.vlan_info[0].Reserved_VLANs}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">System-reserved VLAN IDs</p>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <h4 className="font-medium text-gray-300 mb-2">Possible VLANs</h4>
                  <p className="text-xl font-mono font-semibold text-green-400">
                    {results.vlan_info[0].Possible_VLANs}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Valid range for user-defined VLANs</p>
                </div>
                
                <div className="mt-6 bg-blue-900/20 border border-blue-800/50 rounded-lg p-4 text-sm text-blue-300">
                  <div className="flex">
                    <Info className="flex-shrink-0 h-5 w-5 mr-2" />
                    <div>
                      <p className="mb-2">
                        <span className="font-semibold">VLAN Assignment:</span> The subnet {results.vlan_info[0].Subnet} with mask {results.vlan_info[0].Subnet_Mask} can be divided into multiple VLANs for network segmentation.
                      </p>
                      <p>
                        VLANs provide logical separation of network traffic, enhancing security and performance by isolating broadcast domains.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* VLAN Recommendations */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-300 mb-3">VLAN Design Recommendations</h4>
                  <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-2">VLAN ID</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-2">Purpose</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-2">Subnet Range</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        <tr>
                          <td className="py-2 text-sm text-gray-300">10-19</td>
                          <td className="py-2 text-sm text-gray-300">Management Network</td>
                          <td className="py-2 text-sm font-mono text-gray-300">{results.subnets_info[0].NetworkAddress}/24</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-sm text-gray-300">20-29</td>
                          <td className="py-2 text-sm text-gray-300">User Network</td>
                          <td className="py-2 text-sm font-mono text-gray-300">{results.subnets_info[1]?.NetworkAddress || results.subnets_info[0].NetworkAddress}/24</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-sm text-gray-300">30-39</td>
                          <td className="py-2 text-sm text-gray-300">Voice Network</td>
                          <td className="py-2 text-sm font-mono text-gray-300">{results.subnets_info[2]?.NetworkAddress || results.subnets_info[0].NetworkAddress}/24</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-sm text-gray-300">40-49</td>
                          <td className="py-2 text-sm text-gray-300">Server Network</td>
                          <td className="py-2 text-sm font-mono text-gray-300">{results.subnets_info[3]?.NetworkAddress || results.subnets_info[0].NetworkAddress}/24</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}